export default {
  title: 'Parking',

  vehicles: {
    title: 'Vehicles',
    new_vehicle: 'New Vehicle',
    add: 'Add Vehicle',
    edit: 'Edit Vehicle',
    license_plate: 'License Plate',
    license_plate_placeholder: 'e.g. ABC-1234',
    province: 'Province',
    vehicle_type: 'Vehicle Type',
    brand: 'Brand',
    model: 'Model',
    color: 'Color',
    notes: 'Notes',
    search_placeholder: 'Search plate, brand, model, color, or customer…',
    delete_confirm: 'Are you sure you want to delete this vehicle?',
    delete_has_permits: 'Cannot delete vehicle with active parking permits',

    types: {
      car: 'Car',
      motorcycle: 'Motorcycle',
      pickup: 'Pickup Truck',
      bicycle: 'Bicycle',
      van: 'Van',
      // LPR vehicle_type champion vocabulary (EP-85 warm path)
      suv: 'SUV',
      truck: 'Truck',
      bus: 'Bus',
      vehicle: 'Vehicle',
    },

    empty: {
      title: 'No vehicles registered',
      description: 'Register the first vehicle to start tracking parking permits for this property.',
      no_search_results: 'No vehicles match your search.',
    },

    // EPV-9: parker_type radio + conditional zones
    parker_type: {
      label: 'Parker Type',
      tenant: 'Tenant',
      external: 'External',
    },
    parker_type_hint: {
      tenant: 'Linked to a lease. Monthly invoicing applies',
      external: 'External parker without a lease',
    },
    customer_auto_derived: 'Customer is automatically derived from the selected lease',
    active_permits_warning: 'This vehicle has {{count}} active parking permit(s). Changing the parker type will not automatically update the permits.',
    // P0-01: plate_text + photo fields
    plate_text_label: 'Plate Text (Free-text)',
    plate_text_placeholder: 'e.g. 1กข 1234 or any plate label',
    front_photo_label: 'Front Photo',
    front_photo_hint: 'Photo of the vehicle from the front',
    rear_photo_label: 'Rear Photo',
    rear_photo_hint: 'Photo of the vehicle from the rear',
    photos_section_label: 'Vehicle Photos',
    front_photo_required: 'Front photo is required',
    rear_photo_required: 'Rear photo is required',
    photo_upload_error: 'Failed to upload photo. Please try again.',
  },

  zones: {
    title: 'Parking Zones',
    new_zone: 'New Zone',
    add: 'Add Zone',
    edit: 'Edit Zone',
    name: 'Zone Name',
    zone_type: 'Zone Type',
    total_slots: 'Total Slots',
    occupied_slots: 'Occupied',
    available_slots: 'Available',
    occupancy: 'Occupancy',
    view_all: 'View all',
    full_warning: 'Full',
    delete_confirm: 'Are you sure you want to delete zone "{{name}}"?',
    delete_has_permits: 'Cannot delete zone with active permits',

    types: {
      car: 'Car',
      motorcycle: 'Motorcycle',
      mixed: 'Mixed',
    },

    empty: {
      title: 'No parking zones',
      description: 'Create parking zones to organize and track parking capacity.',
    },

    // Public self-service check-in sticker (EP-105)
    public_checkin: {
      title: 'Public self check-in',
      description:
        'Print this QR as a barrier sticker. Visitors scan it to check in with their plate + photo, then pay on exit.',
      not_generated: 'No check-in link has been generated yet.',
      generate: 'Generate link',
      rotate: 'Regenerate link',
      rotate_warning: 'Regenerating invalidates the current sticker. Reprint after rotating.',
      copy_link: 'Copy link',
      copied: 'Copied!',
      print: 'Print sticker',
      download_png: 'Download PNG',
      link_label: 'Check-in link',
      scan_to_check_in: 'Scan to check in',
      fee_table_title: 'Parking fees',
      fee_daily: 'per day',
      fee_hourly: 'per hour',
      fee_hourly_tier: '≤{{hours}}h',
      fee_all_vehicles: 'All vehicles',
      tiers_title: 'Hourly tier parking fee',
      tiers_description: 'Each tier caps the parking duration; the cheapest applicable tier is charged. Sessions longer than 24h are not settled automatically.',
      tiers_col_hours: 'Max hours',
      tiers_col_amount: 'Amount (THB)',
      tiers_add: 'Add tier',
      tiers_remove: 'Remove tier',
      tiers_count: '{{count}}/6 tiers',
      tier_validation_hours_range: 'Each max_hours must be between 1 and 24.',
      tier_validation_amount_positive: 'Each amount must be zero or positive.',
      tier_validation_sorted: 'Tiers must be sorted ascending by max_hours.',
      tier_validation_unique: 'Each max_hours must be unique.',
    },
  },

  permits: {
    title: 'Parking Permits',
    new_permit: 'New Permit',
    add: 'Add Permit',
    edit: 'Edit Permit',
    permit_type: 'Permit Type',
    slot_number: 'Slot Number',
    sticker_number: 'Sticker/Card Number',
    monthly_fee: 'Monthly Fee',
    daily_fee: 'Daily Fee',
    started_at: 'Start Date',
    ended_at: 'End Date',
    status: 'Status',
    cancel_permit: 'Cancel Permit',
    cancel_confirm: 'Cancel this parking permit? The end date will be set to today.',
    search_placeholder: 'Search license plate...',
    // EPV-10: auto-fill from vehicle.lease_summary
    lease_auto_filled: 'Lease auto-filled from vehicle',
    override_lease: 'Override',

    types: {
      tenant_included: 'Included with Lease',
      tenant_addon: 'Tenant Add-on',
      monthly_external: 'Monthly External',
      daily_external: 'Daily External',
    },

    statuses: {
      active: 'Active',
      expired: 'Expired',
      cancelled: 'Cancelled',
    },

    empty: {
      title: 'No parking permits',
      description: 'Create permits to assign vehicles to parking spots.',
    },
  },

  fee_configs: {
    title: 'Parking Fee Settings',
    vehicle_type: 'Vehicle Type',
    included_spots: 'Included Spots',
    addon_monthly_fee: 'Add-on Monthly (THB)',
    external_monthly_fee: 'External Monthly (THB)',
    external_daily_fee: 'External Daily (THB)',
    save_all: 'Save All',
    saved: 'Fee settings saved successfully',
  },

  daily_logs: {
    title: 'Daily Parking Log',
    record_daily: 'Log Entry',
    add: 'Log Entry',
    license_plate: 'License Plate',
    license_plate_placeholder: 'e.g. กข 1234',
    vehicle_type: 'Vehicle Type',
    parker_name: 'Parker Name',
    parker_phone: 'Phone',
    fee: 'Fee (THB)',
    payment_method: 'Payment Method',
    entered_at: 'Entry Time',
    exited_at: 'Exit Time',
    daily_total: 'Daily Total',
    still_parked: 'Still Parked',
    exit: 'Exit',
    exit_confirm: 'Mark this vehicle as exited? Exit time will be set to now.',
    delete_confirm: 'Are you sure you want to delete this parking log?',
    date_label: 'Date',
    notes: 'Notes',

    payment_methods: {
      cash: 'Cash',
      transfer: 'Transfer',
      promptpay: 'PromptPay',
    },

    empty: {
      title: 'No parking logs',
      description: 'Log daily parking entries for visitors and external vehicles.',
    },
  },

  lpr: {
    title: 'LPR Events',
    description: 'License plate detections from cameras, auto-refreshes every 10 seconds.',
    // New schema fields (P2-04)
    captured_at: 'Captured At',
    plate_text: 'Plate',
    camera: 'Camera',
    best_candidate: 'Best Candidate',
    verification_status: 'Status',
    thumbnail: 'Photo',
    thumbnail_alt: 'Vehicle thumbnail',
    thumbnail_unavailable: 'Thumbnail unavailable',
    // Legacy fields
    detected_at: 'Detected At',
    license_plate: 'License Plate',
    direction: 'Direction',
    status: 'Status',
    confidence: 'Confidence',

    directions: {
      entry: 'Entry',
      exit: 'Exit',
      // EP-106: raw ML classifier values stored in lpr_events.direction
      in: 'In',
      out: 'Out',
      parked: 'Parked',
      unclear: 'Unclear',
    },

    // EP-106: champion vehicle_type classifier (7 classes)
    vehicle_types: {
      car: 'Car',
      suv: 'SUV',
      pickup: 'Pickup',
      truck: 'Truck',
      bus: 'Bus',
      motorcycle: 'Motorcycle',
      unknown: 'Unknown',
    },

    // EP-106: champion vehicle_color classifier (17 classes)
    colors: {
      white: 'White',
      black: 'Black',
      silver: 'Silver',
      grey: 'Grey',
      red: 'Red',
      blue: 'Blue',
      brown: 'Brown',
      yellow: 'Yellow',
      green: 'Green',
      orange: 'Orange',
      purple: 'Purple',
      pink: 'Pink',
      beige: 'Beige',
      gold: 'Gold',
      'two-tone': 'Two-tone',
      other: 'Other',
      unknown: 'Unknown',
    },

    statuses: {
      unprocessed: 'Unprocessed',
      matched: 'Matched',
      unmatched: 'Unmatched',
      error: 'Error',
    },

    verification_statuses: {
      pending: 'Pending',
      verified_success: 'Verified',
      verified_failed: 'Failed',
    },

    // T148-007 (EP-148 Part B): unpaid-candidate visibility, spec §5.
    candidate: {
      badge: 'Unpaid candidate',
      resolved_paid: 'Resolved: paid',
      session_label: 'Session #{{id}}',
      tooltip: 'Distance {{distance}} · confidence {{confidence}}%',
    },

    // T148A-007 (EP-148 Amendment A, ruling i): operator "Settle & check out"
    // action on an unresolved unpaid-candidate row.
    settle: {
      action: 'Settle & check out',
      modal_title: 'Settle parking session #{{id}}',
      amount_label: 'Amount (THB)',
      payment_method_label: 'Payment method',
      payment_method_cash: 'Cash',
      payment_method_transfer: 'Transfer',
      cancel: 'Cancel',
      save: 'Settle & check out',
      success: 'Session settled and checked out',
      error_already_settled: 'This session was already settled',
      error_generic: 'Could not settle this session. Please try again.',
    },

    filters: {
      all: 'All',
      pending: 'Pending',
      verified: 'Verified',
      failed: 'Failed',
      all_statuses: 'All Statuses',
      date_from: 'From',
      date_to: 'To',
      filter_label: 'Filters',
      // EP-106: attribute filter chrome keys
      plate_placeholder: '1กก 1234',
      vehicle_type: 'Vehicle Type',
      color: 'Color',
      direction: 'Direction',
      clear: 'Clear',
      clear_plate: 'Clear plate filter',
      active_count: '{{count}}',
      widen_date_hint: 'No results. Try widening the date range.',
      // T148-007: standalone toggle, orthogonal to the verification chips above.
      candidates_only: 'Candidates only',
    },

    empty: {
      title: 'No LPR events',
      description: 'License plate detections will appear here when the LPR service is active.',
    },

    verification_drawer: {
      title: 'Verify Event',
      aria_label: 'LPR event verification',
      captured_event: 'Captured Event',
      candidate_vehicle: 'Candidate Vehicle',
      front: 'Front',
      rear: 'Rear',
      registered_front: 'Registered Front',
      registered_rear: 'Registered Rear',
      registered_photo_alt: 'Registered vehicle photo',
      no_registered_photo: 'No registered photo',
      vehicle_type: 'Type',
      color: 'Color',
      last_seen: 'Last Seen',
      vehicle_not_found: 'Vehicle not found',
      verify_success: 'Verify Success',
      verify_failed: 'Verify Failed',
      leave_unmapped: 'Leave Unmapped',
      select_reasons: 'Select failure reasons',
      submit_failed: 'Submit',
      note_placeholder: 'Optional note...',
      last_verified_by: 'Last verified by {{name}} at {{time}}',
      link_vehicle: 'Link a vehicle',
      search_vehicle_placeholder: 'Search plate or brand…',
      search_min_chars: 'Type at least 2 characters',
      no_vehicles_found: 'No matching vehicles',
      clear_selection: 'Clear',
      searching_vehicles: 'Searching vehicles…',
      link_and_verify: 'Link & verify',
      reasons: {
        text: 'Plate text mismatch',
        color: 'Color mismatch',
        type: 'Vehicle type mismatch',
        blurry: 'Blurry image',
        occluded: 'Occluded / obstructed',
        dark: 'Too dark / low light',
        glare: 'Glare / reflection',
        no_plate: 'No plate visible',
        not_a_vehicle: 'Not a vehicle / bad crop',
        partial: 'Partial / cut-off',
        duplicate: 'Duplicate capture',
        unknown: 'Unknown reason',
      },
    },

    reconciliation_table: {
      section_label: 'Attribute comparison',
      attribute: 'Attribute',
      ml_to_registered: 'ML → Registered',
      brand_model: 'Brand / Model',
      direction_label: 'Operator direction',
      match: '(match)',
      mismatch: '(mismatch)',
    },
  },

  reports: {
    occupancy_title: 'Parking Occupancy Report',
    revenue_title: 'Parking Revenue Report',
    registry_title: 'Vehicle Registry Report',
    total_vehicles: 'Total Vehicles',
    monthly_revenue: 'Monthly Revenue',
    expiring_permits: 'Expiring Permits',
    expiring_soon: '{{count}} expiring in {{days}} days',
  },

  // EP-161: ที่จอดรถวันนี้, the day's visitor cars of one property.
  sessions: {
    title: 'Parking today',
    states: {
      parked: 'Parked',
      overstay: 'Overstayed',
      awaiting_payment: 'Awaiting payment',
      slip_to_verify: 'Slip to check',
      rejected: 'Rejected',
      paid: 'Paid',
    },
    tabs: {
      all: 'All',
      aria_label: 'Status',
    },
    search_plate: 'Search plate',
    date_label: 'Date',
    all_days: 'All days',
    plate: 'Plate',
    zone: 'Zone',
    entered: 'In',
    exited: 'Out',
    amount: 'Fee',
    state: 'Status',
    actions: 'Actions',
    view_slip: 'View slip',
    verify: 'Verify',
    reject: 'Reject',
    fraud_mark: 'Mark as fraud',
    fraud_badge: 'Fraud',
    plate_flagged: 'This plate was marked as fraud before',
    slip: 'Slip',
    car_photo: 'Car photo at payment',
    entry_photo: 'Entry photo',
    cash_photo: 'Cash photo',
    server_time: '(server time)',
    history: 'Decision history',
    reference: 'Reference',
    reject_reason: 'Reason',
    fraud_by: 'Marked by {{name}}',
    show_detail: 'Show details',
    hide_detail: 'Hide details',
    verified_by: {
      slip_checked: 'Slip checked',
      cash: 'Cash taken',
      transfer: 'Transfer received',
      bank_match: 'Matched to the bank statement',
      ml: 'Verified automatically',
    },
    empty: 'No cars in this list',
    fraud_modal: {
      title: 'Mark as fraud',
      body: "This car's payment is rejected and its plate is recorded.",
      reason_label: 'Reason',
      submit: 'Mark as fraud',
      success: 'Marked as fraud',
    },
    errors: {
      reason_required: 'Enter a reason.',
      already_flagged: 'This car is already marked as fraud.',
      already_paid: 'This car has paid and cannot be marked as fraud.',
      not_checked_out: 'The car is still parked. Check it out first.',
    },
    // EP-161 §2.3, S6 (T161-W02a): the gate, in the operator app only.
    gate: {
      check_out: 'Check out',
      use_mobile_app: 'Use the FlowRent mobile app',
      sheet_title: 'Check out {{plate}}',
      entered_at: 'In {{time}}',
      duration: 'Parked {{duration}}',
      duration_hours_minutes: '{{hours}} h {{minutes}} min',
      duration_minutes: '{{minutes}} min',
      charge: 'Parking fee {{amount}}',
      scan_to_pay: 'Ask the driver to scan and pay',
      qr_label: 'PromptPay QR for the parking fee',
      no_qr: 'This charge has no QR. Take cash or check the transfer instead.',
      take_cash: 'Take cash',
      verify: 'Verify',
      verify_at_gate_reason: 'Checked at the gate',
      close: 'Close',
      cash_title: 'Take cash {{plate}}',
      cash_amount_from_charge: 'Amount {{amount}} (from the parking fee)',
      cash_amount_label: 'Amount (THB)',
      take_cash_photo: 'Take a photo of the cash',
      cash_photo_hint: 'Take a photo of the cash before saving',
      uploading_photo: 'Sending the photo',
      retake: 'Retake',
      save: 'Save',
      cancel: 'Cancel',
      cash_saved: 'Cash payment saved',
      errors: {
        cash_photo_required: 'Take a photo of the cash before saving',
        cash_photo_invalid: 'This photo cannot be used. Take it again.',
        cash_photo_stale: 'This photo is too old. Take it again.',
        already_settled: 'This car has already paid',
        already_paid: 'This car has already paid',
        invalid_amount: 'The amount must be more than 0',
        amount_required: 'Enter the amount',
        already_exited: 'This car has already checked out',
        no_fee_configured: 'No parking fee is set for this zone',
        overstay_contact_admin: 'The car stayed longer than the kiosk can charge. Take cash for the agreed amount.',
        upload_failed: 'The photo did not send. Try again.',
        check_out_failed: 'Could not check out. Try again.',
        failed: 'Could not save. Try again.',
      },
    },
  },

  list_page: {
    select_property: 'Select Property',
    no_property_selected: 'No property selected',
    parking_modules: 'Parking Modules',
  },

  labels: {
    license_plate: 'License Plate',
    vehicle_type: 'Vehicle Type',
    brand: 'Brand',
    model: 'Model',
    color: 'Color',
    province: 'Province',
    zone_name: 'Zone Name',
    zone_type: 'Zone Type',
    total_slots: 'Total Slots',
    occupied_slots: 'Occupied',
    available_slots: 'Available',
    slot_number: 'Slot Number',
    sticker_number: 'Sticker/Card Number',
    permit_type: 'Permit Type',
    monthly_fee: 'Monthly Fee',
    daily_fee: 'Daily Fee',
    started_at: 'Start Date',
    ended_at: 'End Date',
    status: 'Status',
    parker_name: 'Parker Name',
    parker_phone: 'Phone',
    fee: 'Fee (THB)',
    payment_method: 'Payment Method',
    entered_at: 'Entry Time',
    exited_at: 'Exit Time',
    notes: 'Notes',
    manage_vehicles: 'Register and track vehicles',
    manage_zones: 'Configure parking areas',
    manage_permits: 'Assign parking permits',
    manage_daily_logs: 'Record daily visitor parking',
    manage_fees: 'Configure parking rates',
  },

  actions: {
    new_vehicle: 'New Vehicle',
    new_zone: 'New Zone',
    new_permit: 'New Permit',
    record_daily: 'Log Entry',
    cancel_permit: 'Cancel Permit',
  },

  messages: {
    vehicle_created: 'Vehicle registered',
    vehicle_updated: 'Vehicle updated',
    vehicle_deleted: 'Vehicle deleted',
    zone_created: 'Parking zone created',
    zone_updated: 'Parking zone updated',
    zone_deleted: 'Parking zone deleted',
    permit_created: 'Parking permit created',
    permit_updated: 'Parking permit updated',
    permit_cancelled: 'Parking permit cancelled',
    log_created: 'Parking log recorded',
    log_updated: 'Parking log updated',
    log_deleted: 'Parking log deleted',
    fee_config_saved: 'Fee settings saved successfully',
    zone_at_capacity_warning: 'This zone is at full capacity',
  },

  filters: {
    all: 'All',
    all_types: 'All Types',
    all_statuses: 'All Statuses',
    active: 'Active',
    expired: 'Expired',
    cancelled: 'Cancelled',
    date_from: 'From',
    date_to: 'To',
  },

  validation: {
    license_plate_required: 'License plate is required',
    vehicle_type_required: 'Vehicle type is required',
    duplicate_license_plate: 'A vehicle with this license plate already exists',
    monthly_fee_required: 'Monthly fee is required',
    started_at_required: 'Start date is required',
    total_slots_min: 'Total slots must be at least 1',
    zone_at_capacity_warning: 'This zone is at full capacity',
    included_spots_non_negative: 'Must be a non-negative integer',
    fee_non_negative: 'Must be 0 or greater',
    // EPV-9: parker_type validation
    lease_required: 'Lease is required for tenant vehicles',
    customer_required_for_external: 'Customer is required for external vehicles',
    // P0-01: plate_text validation
    plate_text_required: 'Plate text is required',
  },

  setup_guide: {
    title: 'Get Started with Parking',
    description: 'Track vehicles, manage parking zones, and issue parking permits.',
    prerequisites_title: 'Prerequisites',
    prereq_property: 'At least 1 property',
    prereq_setup_property: 'Set up a property',
    steps: {
      create_zone: {
        title: 'Create parking zones',
        description: 'Define parking areas with capacity limits',
      },
      register_vehicle: {
        title: 'Register vehicles',
        description: 'Add tenant and visitor vehicles',
      },
      issue_permit: {
        title: 'Issue permits',
        description: 'Assign parking spots to vehicles',
      },
    },
    empty: {
      title: 'No parking data yet',
      description: 'Start by creating parking zones for this property.',
    },
    zones: {
      title: 'Get Started with Parking Zones',
      description: 'Define parking areas with capacity limits to organize your property\'s parking.',
      steps: {
        create_zone: {
          title: 'Create a parking zone',
          description: 'Define a zone name and type (car, motorcycle, or mixed)',
        },
        set_capacity: {
          title: 'Set zone capacity',
          description: 'Specify the total number of parking slots available',
        },
        monitor: {
          title: 'Monitor occupancy',
          description: 'Track slot usage and availability in real-time',
        },
      },
    },
    permits: {
      title: 'Get Started with Parking Permits',
      description: 'Assign parking spots to tenants and external parkers.',
      prereq_vehicles: 'At least 1 vehicle registered',
      prereq_setup_vehicles: 'Register a vehicle',
      prereq_zones: 'At least 1 parking zone created',
      prereq_setup_zones: 'Create a zone',
      steps: {
        register_vehicle: {
          title: 'Register vehicles',
          description: 'Add tenant and visitor vehicles first',
        },
        create_zone: {
          title: 'Create parking zones',
          description: 'Define parking areas with capacity limits',
        },
        issue_permit: {
          title: 'Issue permits',
          description: 'Assign parking spots and set permit type and fees',
        },
      },
    },
    daily_logs: {
      title: 'Get Started with Daily Parking',
      description: 'Track and charge daily visitor parking entries.',
      prereq_fee_configs: 'Fee rates configured',
      prereq_setup_fees: 'Configure fees',
      prereq_zones: 'At least 1 parking zone created',
      prereq_setup_zones: 'Create a zone',
      steps: {
        configure_fees: {
          title: 'Configure fee rates',
          description: 'Set daily fees by vehicle type',
        },
        log_entry: {
          title: 'Log vehicle entries',
          description: 'Record license plate, vehicle type, and entry time',
        },
        record_exit: {
          title: 'Record exits',
          description: 'Mark vehicles as exited and collect payment',
        },
      },
    },
  },

  errors: {
    validation_failed: 'Validation failed. Please check the form and try again.',
    duplicate_permit: 'A permit for this vehicle already exists.',

    fields: {
      permit_type: 'Permit type',
      vehicle_id: 'Vehicle',
      parking_zone_id: 'Parking zone',
      lease_id: 'Lease',
      monthly_fee: 'Monthly fee',
      daily_fee: 'Daily fee',
      started_at: 'Start date',
      ended_at: 'End date',
      license_plate: 'License plate',
    },

    codes: {
      blank: "can't be blank",
      taken: 'is already taken',
      invalid: 'is invalid',
      too_short: 'is too short',
      too_long: 'is too long',
      greater_than: 'must be greater than the limit',
      greater_than_or_equal_to: 'must be greater than or equal to the limit',
      less_than: 'must be less than the limit',
      less_than_or_equal_to: 'must be less than or equal to the limit',
      not_a_number: 'is not a number',
      inclusion: 'is not included in the list',
    },
    // EPV-10: client-side permit_type vs vehicle parker_type mismatch
    permit_type_mismatch_with_external_vehicle: 'Tenant permit types (tenant_included, tenant_addon) cannot be assigned to an external vehicle',
  },
}
